import type { Character } from '../domain/character.ts';
import type { RuntimeState } from '../domain/runtime.ts';
import { encounterActionProblem } from './encounter.ts';
import type { InventoryCatalogue, InventoryEntry } from './inventory.ts';
import { handPlacementFor, holdProblem, unallocatedCopies } from './inventory.ts';
import type { Hotlist } from './hotlist.ts';
import { allocationProblem, hotlistHomeForReturn, hotlistSlotIndexProblem } from './hotlist.ts';

/**
 * RULE DCC-HOT-007 / DCC-HOT-008 - readying or exchanging a Hotlist Item, the
 * non-consumable half of the **Use a Hotlist Item** Action.
 *
 * Rules p.19, Table 2: "Use a Hotlist Item - Use a prepared consumable item or
 * ready another Hotlist Item."
 *
 * Rules p.37: "A crawler selects a consumable item from their Hotlist, such as
 * a potion or scroll, and uses it or withdraws another item. Remember that
 * swapping weapons is free as part of the Attack Action."
 *
 * Rules p.48: "A crawler can swap an item in their hands for the item in the
 * Hotlist as part of the Use a Hotlist Item Action." Note ITEM, not weapon -
 * the weapon case is the free carve-out that E3 already built, and this is the
 * general paid one.
 *
 * RULE DCC-HOT-023 - PROJECT READING, INFORMED BY THE SOURCE. C&C p.15's
 * "YOUR HOTLIST" sidebar (invisible to text extraction; read as a page image):
 * "When you have a moment outside of combat, or an Action in combat, any item
 * in your Hotlist can instantly appear in a free hand, ready to use during that
 * same moment or Action. You may instead have the item appear on the ground in
 * front of you."
 *
 * Two operations are printed, and BOTH remain available to the player:
 *
 *  - **READY** - the Item appears in available hand capacity;
 *  - **EXCHANGE** - a specifically chosen held Item is put back into the
 *    Hotlist as the new one comes out (Rules p.48).
 *
 * Having a free hand does NOT remove the ability to exchange. A crawler holding
 * a torch with one hand free may deliberately want the torch stowed rather than
 * a shield placed beside it; those are different outcomes and only the player
 * knows which was meant. **This file therefore never chooses an outgoing Item.**
 * An exchange names its outgoing Item explicitly or it is refused.
 *
 * NOT BUILT, and printed: "the item appear on the ground in front of you."
 * There is no floor, no dropped-item container and no way to pick anything back
 * up in this application, so building it would mean inventing a container model
 * inside an Action slice. Recorded in `docs/HOTLIST_AUDIT.md`.
 *
 * PURE. Nothing here spends an Action, rolls, writes or consumes. It returns
 * the next Character for the application service to carry through the ordinary
 * persistence boundary. Consuming a supported consumable is NOT this file's
 * job - that is `itemUse.ts` and `itemUseService.ts`, built and verified in E4,
 * and it must not be reimplemented here.
 */

/**
 * What the player asked for.
 *
 * A CLOSED UNION CARRYING FULL IDENTITY. `{ slot, itemId }` was enough for E3
 * because a free weapon swap has exactly one possible outgoing weapon (E3
 * refuses outright when a crawler holds two). A paid exchange does not: the
 * player picks WHICH held Item is put away, so the choice is not fully
 * described without it, and "whatever is in that hand now" is not what they
 * agreed to.
 *
 * Every part is re-checked against the live state at execution time. A slot
 * whose contents changed refuses; an outgoing Item that is no longer held
 * refuses; a DIFFERENT held Item that has since appeared is never substituted.
 */
export type HotlistUseChoice =
  /** The Item appears in available hand capacity (C&C p.15). */
  | { kind: 'ready'; slot: number; itemId: string }
  /** A named held Item goes back to the Hotlist as this one comes out (p.48). */
  | { kind: 'exchange'; slot: number; itemId: string; outgoingItemId: string };

export interface HotlistUsePlan {
  choice: HotlistUseChoice;
  /** The Hotlist slot the Item came out of. */
  slot: number;
  incomingItemId: string;
  /** Null for a ready; the Item put away for an exchange. */
  outgoingItemId: string | null;
  /** Where the outgoing Item landed, or null when nothing was put away. */
  outgoingSlot: number | null;
}

export type HotlistUseOutcome =
  | { ok: true; character: Character; plan: HotlistUsePlan }
  | { ok: false; reason: string };

/** The Item a Hotlist slot names, when that slot holds an Item at all. */
function itemEntryAt(hotlist: Hotlist, slot: number): { itemId: string; quantity: number } | null {
  const entry = hotlist[slot] ?? null;
  if (entry === null || entry.kind !== 'item') return null;
  return { itemId: entry.itemId, quantity: entry.quantity };
}

/**
 * Whether one more copy of this Item can be taken into a hand right now.
 *
 * RULE DCC-INV-018. **`Item.slot` is NOT asked whether a hand can hold the
 * thing.** That field says where an Item is WORN, which is a different
 * question, and treating it as a holdability test was wrong: C&C p.15 says
 * "ANY item in your Hotlist can instantly appear in a free hand." A helmet can
 * be carried without being worn, a potion can be carried without being gear at
 * all, and a rope with no Gear Slot is still a rope you can hold.
 *
 * So this asks CAPACITY, through the shared rules:
 *
 *  - a held-Hands Item (sword, shield) goes into its own Gear Slot, so
 *    `equipProblem` answers, including the two-handed exclusion;
 *  - anything else is carried LOOSELY, so `holdProblem` answers, against the
 *    same `slotUsage` hand count.
 *
 * `handPlacementFor` makes that choice in one place so no pathway can invent a
 * second answer, and `holdProblem` routes back to `equipProblem` if handed a
 * gear-slot Item anyway.
 */
export function canBeReadied(
  inventory: readonly InventoryEntry[],
  itemId: string,
  catalogue: InventoryCatalogue,
): { ok: true } | { ok: false; reason: string } {
  const item = catalogue.lookupItem(itemId);
  if (!item) return { ok: false, reason: 'This installation does not have that Item.' };
  const problem = holdProblem(inventory, itemId, catalogue);
  return problem === null ? { ok: true } : { ok: false, reason: problem };
}

/** The distinct Items a crawler currently HOLDS - the eligible outgoing set. */
export function heldItemIds(
  inventory: readonly InventoryEntry[],
  catalogue: InventoryCatalogue,
): string[] {
  return inventory
    .filter((entry) => {
      const item = catalogue.lookupItem(entry.itemId);
      if (item === undefined) return false;
      // RULE DCC-INV-018 - a hand is a hand. A wielded sword lives in
      // `equipped` because that IS its Gear Slot; a carried helmet lives in
      // `held`. Both can be put away in an exchange.
      return handPlacementFor(item) === 'gear-slot' ? entry.equipped > 0 : entry.held > 0;
    })
    .map((entry) => entry.itemId);
}

/** Which count a copy of this Item occupies a hand through. */
function handFieldFor(itemId: string, catalogue: InventoryCatalogue): 'equipped' | 'held' | null {
  const item = catalogue.lookupItem(itemId);
  if (item === undefined) return null;
  return handPlacementFor(item) === 'gear-slot' ? 'equipped' : 'held';
}

/**
 * Works out the withdrawal and returns the crawler afterwards, or refuses.
 *
 * Legality is ASKED OF THE EXISTING RULES rather than re-decided here, exactly
 * as E3 does: `equipProblem` answers hand capacity and the two-handed exclusion
 * (RULE DCC-INV-016), `hotlistHomeForReturn` answers where the outgoing Item
 * goes (RULE DCC-HOT-012), and `allocationProblem` answers
 * `equipped + held + hotlisted <= owned` (RULE DCC-INV-019).
 *
 * A refusal costs nothing: this writes nothing and the service spends the
 * Action only after this has succeeded.
 */
export function planHotlistUse(
  character: Character,
  choice: HotlistUseChoice,
  catalogue: InventoryCatalogue,
): HotlistUseOutcome {
  const slotProblem = hotlistSlotIndexProblem(choice.slot);
  if (slotProblem) return { ok: false, reason: slotProblem };

  // IDENTITY FIRST. A slot is a position whose contents change; the player
  // agreed to an Item, not to a position. Same lesson as E3's `SwapChoice`.
  const incoming = itemEntryAt(character.hotlist, choice.slot);
  if (incoming === null || incoming.itemId !== choice.itemId) {
    return {
      ok: false,
      reason:
        `Hotlist slot ${choice.slot + 1} no longer holds that Item. Nothing has been changed - ` +
        'check the slot and try again.',
    };
  }

  const incomingItem = catalogue.lookupItem(incoming.itemId);
  if (incomingItem === undefined) {
    return { ok: false, reason: 'This installation does not have that Item.' };
  }
  const incomingName = incomingItem.name;
  const incomingField = handFieldFor(incoming.itemId, catalogue);

  // THE ALLOCATION INVARIANT, on the whole slot rather than on one copy. E2
  // keeps an over-allocated slot loadable, visible and INERT; readying from one
  // would execute exactly the entry E2 promised does nothing, and leave
  // `equipped + held + hotlisted` above what is owned by operation rather than by
  // history. Not repaired here either - shrinking the slot would edit what the
  // player did not ask to change.
  const ownedEntry = character.inventory.find((e) => e.itemId === incoming.itemId);
  const available = ownedEntry === undefined ? 0 : unallocatedCopies(ownedEntry);
  if (available < incoming.quantity) {
    return {
      ok: false,
      reason:
        `Hotlist slot ${choice.slot + 1} claims ${incoming.quantity} ${incomingName} but only ` +
        `${available} ${available === 1 ? 'is' : 'are'} free. Sort the Hotlist out first; a slot ` +
        'that does not match what the crawler carries cannot be used.',
    };
  }

  let working: readonly InventoryEntry[] = character.inventory;
  const hotlist: Hotlist = [...character.hotlist];
  let outgoingSlot: number | null = null;
  let outgoingItemId: string | null = null;

  if (choice.kind === 'exchange') {
    outgoingItemId = choice.outgoingItemId;
    if (outgoingItemId === incoming.itemId) {
      return {
        ok: false,
        reason: `${incomingName} is already in hand; exchanging it for itself does nothing.`,
      };
    }
    const outgoingEntry = working.find((e) => e.itemId === outgoingItemId);
    const outgoingItem = catalogue.lookupItem(outgoingItemId);
    const outgoingName = outgoingItem?.name ?? outgoingItemId;
    const outgoingField = handFieldFor(outgoingItemId, catalogue);
    // The chosen outgoing Item must STILL be in a hand - EITHER as gear in its
    // own Hands slot or carried loosely. If it is not, this refuses; it never
    // falls back to whatever else happens to be in a hand.
    if (
      outgoingEntry === undefined ||
      outgoingItem === undefined ||
      outgoingField === null ||
      outgoingEntry[outgoingField] <= 0
    ) {
      return {
        ok: false,
        reason:
          `${outgoingName} is not in this crawler's hands, so it cannot be put away. Nothing has ` +
          'been changed - choose again.',
      };
    }

    // RULE DCC-HOT-012 - the outgoing Item goes INTO the Hotlist, not the
    // floor. Shared with E3 so the two pathways cannot answer differently.
    const home = hotlistHomeForReturn(hotlist, choice.slot, incoming.quantity, outgoingItemId);
    if ('reason' in home) return { ok: false, reason: home.reason };

    // Put it away FIRST, in memory, so the hand it frees is available to the
    // incoming Item. A one-handed-for-two-handed exchange depends on this
    // ordering and would otherwise be refused for no reason.
    working = working.map((entry) =>
      entry.itemId === outgoingItemId
        ? { ...entry, [outgoingField]: entry[outgoingField] - 1 }
        : entry,
    );

    if (home.merge) {
      const existing = hotlist[home.slot] ?? null;
      if (existing !== null && existing.kind === 'item') {
        hotlist[home.slot] = { ...existing, quantity: existing.quantity + 1 };
      }
    } else {
      hotlist[home.slot] = { kind: 'item', itemId: outgoingItemId, quantity: 1 };
    }
    outgoingSlot = home.slot;
  }

  // One copy leaves the Hotlist, whichever operation this is.
  if (incoming.quantity === 1) {
    // Emptied - unless the outgoing Item just took this slot.
    if (outgoingSlot !== choice.slot) hotlist[choice.slot] = null;
  } else {
    hotlist[choice.slot] = {
      kind: 'item',
      itemId: incoming.itemId,
      quantity: incoming.quantity - 1,
    };
  }

  // Asked of the SHARED rule, against the inventory as it will be. This is what
  // refuses a two-handed Item while a shield is still held, and what ALLOWS it
  // once the chosen exchange has freed the hand.
  const cannotTake = holdProblem(working, incoming.itemId, catalogue);
  if (cannotTake !== null) return { ok: false, reason: cannotTake };

  if (incomingField === null) {
    return { ok: false, reason: 'This installation does not have that Item.' };
  }
  working = working.map((entry) =>
    entry.itemId === incoming.itemId
      ? { ...entry, [incomingField]: entry[incomingField] + 1 }
      : entry,
  );

  // THE INVARIANT, on the state this would actually produce, for the Items this
  // operation TOUCHED and no others. An unrelated over-allocated potion slot is
  // somebody else's problem and must not block a legal readying.
  for (const touched of [incoming.itemId, outgoingItemId]) {
    if (touched === null) continue;
    const entry = working.find((e) => e.itemId === touched);
    if (entry === undefined) continue;
    const name = catalogue.lookupItem(touched)?.name ?? touched;
    const problem = allocationProblem(hotlist, touched, name, entry.quantity, entry.equipped, entry.held);
    if (problem !== null) return { ok: false, reason: problem };
  }

  return {
    ok: true,
    character: { ...character, inventory: [...working], hotlist },
    plan: {
      choice,
      slot: choice.slot,
      incomingItemId: incoming.itemId,
      outgoingItemId,
      outgoingSlot,
    },
  };
}

/**
 * Whether the crawler can AFFORD a Hotlist Action right now, or the reason not.
 *
 * SHARED, and that is the whole point. The service asks it authoritatively and
 * the panel asks it to decide whether Ready / Swap is live, so a control is
 * never offered for something certain to be refused - the same shape
 * `equipProblem` and `quantityProblem` already take. React does no Action
 * arithmetic of its own.
 *
 * Rules p.48: one Action in a fight. C&C p.15: a free moment outside one, so
 * out of combat there is nothing to afford and this returns null.
 *
 * The service RE-ASKS this at execution time regardless. A rendered control is
 * a description of a moment that has passed.
 */
export function hotlistActionAffordability(
  runtime: RuntimeState | null,
  characterId: string,
  fighting: boolean,
): string | null {
  if (!fighting) return null;
  // RULE DCC-ENC-003 - phase as well as count, through the same authority the
  // service uses. Asking only about the count made the menu lie in a new way
  // after Foundation A: Ready / Swap sat live during Mob Resolution beside a
  // service certain to refuse it.
  const problem = encounterActionProblem(runtime, characterId, 1, 'normal');
  if (problem === null) return null;
  const left = runtime?.encounter?.actionsRemaining[characterId] ?? 0;
  return left >= 1
    ? problem
    : 'No Actions left this round. Readying or swapping a Hotlist Item costs one ' +
        '(Rules p.19, p.48).';
}

/** One legal, fully explicit thing the player could do right now. */
export interface HotlistUseOption {
  choice: HotlistUseChoice;
  /** What comes out of the Hotlist. */
  incomingName: string;
  /** What goes back, or null for a ready. */
  outgoingName: string | null;
  /** The crawler this would produce. Pure - nothing is written. */
  character: Character;
}

/**
 * Every legal explicit choice, right now.
 *
 * THE MENU MUST NOT LIE, and it must not GUESS. Each option is a complete
 * instruction that `planHotlistUse` has already accepted against this exact
 * crawler, so a control is never offered for an operation the service will
 * refuse, and the service is never handed an under-specified request it would
 * have to resolve by picking something for the player.
 *
 * When several held Items could be put away, every one of them is a separate
 * option. That is the point: the application does not choose.
 */
export function hotlistUseOptionsFor(
  character: Character,
  catalogue: InventoryCatalogue,
): HotlistUseOption[] {
  const held = heldItemIds(character.inventory, catalogue);
  const options: HotlistUseOption[] = [];

  character.hotlist.forEach((entry, slot) => {
    if (entry === null || entry.kind !== 'item') return;
    const incomingName = catalogue.lookupItem(entry.itemId)?.name ?? entry.itemId;

    const candidates: HotlistUseChoice[] = [
      { kind: 'ready', slot, itemId: entry.itemId },
      ...held
        .filter((outgoingItemId) => outgoingItemId !== entry.itemId)
        .map(
          (outgoingItemId): HotlistUseChoice => ({
            kind: 'exchange',
            slot,
            itemId: entry.itemId,
            outgoingItemId,
          }),
        ),
    ];

    for (const choice of candidates) {
      const planned = planHotlistUse(character, choice, catalogue);
      if (!planned.ok) continue;
      options.push({
        choice,
        incomingName,
        outgoingName:
          choice.kind === 'exchange'
            ? (catalogue.lookupItem(choice.outgoingItemId)?.name ?? choice.outgoingItemId)
            : null,
        character: planned.character,
      });
    }
  });

  return options;
}

/** Whether two choices are the SAME instruction, in every part. */
export function sameHotlistUseChoice(a: HotlistUseChoice, b: HotlistUseChoice): boolean {
  if (a.kind !== b.kind || a.slot !== b.slot || a.itemId !== b.itemId) return false;
  if (a.kind === 'exchange' && b.kind === 'exchange') {
    return a.outgoingItemId === b.outgoingItemId;
  }
  return true;
}

/**
 * The still-valid option for a remembered choice, or null.
 *
 * Legality asks "could the crawler do this?"; identity asks "is this still the
 * thing they asked for?" Both must hold, and identity now includes the outgoing
 * Item - otherwise a choice made while a torch was held could execute against a
 * shield picked up since.
 */
export function resolveHotlistUseChoice(
  options: readonly HotlistUseOption[],
  choice: HotlistUseChoice | null,
): HotlistUseOption | null {
  if (choice === null) return null;
  return options.find((option) => sameHotlistUseChoice(option.choice, choice)) ?? null;
}
